import Icon from "@components/ui/icon";
import Popover from "@components/ui/popover";
import Tooltip from "@components/ui/tooltip";
import { cn } from "@lib/cn";
import { useMemo, useState } from "react";
import { useAiImageStoreShallow } from "../store";

export const AiImageStyleSelect = ({
  options,
}: {
  options: readonly {
    label: string;
    value: string;
    image?: string;
    shortLabel?: string;
  }[];
}) => {
  const [open, setOpen] = useState(false);
  const { style, setStyle } = useAiImageStoreShallow((s) => ({
    style: s.style,
    setStyle: s.setStyle,
  }));

  const selectedOption = useMemo(() => {
    return options.find((s) => s.value === style);
  }, [options, style]);

  return (
    <Popover open={open} onOpenChange={setOpen} placement="left">
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label="Style"
          className="tw-w-full tw-text-left tw-px-3 tw-h-8 tw-justify-between tw-flex tw-items-center tw-rounded-lg tw-text-xs tw-font-normal tw-text-blue tw-transition-colors hover:tw-bg-blue-50"
        >
          Style
          <span className="tw-flex tw-items-center tw-gap-2 tw-text-blue-500">
            <span className="tw-truncate tw-max-w-24">
              {selectedOption?.shortLabel ||
                selectedOption?.label ||
                "Automatic"}
            </span>
            <Icon icon="ri-arrow-right-s-line" className="tw-text-blue-700" />
          </span>
        </button>
      </Popover.Trigger>
      <Popover.Content className="tw-z-[99999] tw-p-1">
        <div className="tw-p-2 tw-rounded-xl tw-bg-white tw-grid tw-grid-cols-3 tw-gap-2 tw-shadow-popover tw-border tw-border-solid tw-border-blue/5">
          <Tooltip>
            <Tooltip.Trigger asChild>
              <button
                aria-label="Automatic"
                type="button"
                className={cn(
                  "tw-outline-none tw-rounded-lg tw-size-[72px] tw-outline-2 tw-bg-blue-50 tw-border tw-border-solid tw-border-blue/10 tw-outline-offset-1 tw-transition-[outline] hover:tw-outline-primary tw-flex tw-items-center tw-justify-center",
                  {
                    "tw-outline-primary": style === "none",
                  },
                )}
                onClick={() => {
                  setStyle("none");
                  setOpen(false);
                }}
              >
                <Icon
                  icon="ri-brush-ai-line"
                  className="tw-size-6 tw-text-2xl/none tw-text-blue-500"
                />
              </button>
            </Tooltip.Trigger>
            <Tooltip.Content className="tw-z-[99999]">
              <Tooltip.Text className="tw-max-w-60 tw-text-center">
                Automatic
              </Tooltip.Text>
            </Tooltip.Content>
          </Tooltip>
          {options.map((s) => (
            <Tooltip key={s.value}>
              <Tooltip.Trigger asChild>
                <button
                  aria-label={s.label}
                  type="button"
                  onClick={() => {
                    setStyle(s.value);
                    setOpen(false);
                  }}
                  className={cn(
                    "tw-outline-none tw-rounded-lg tw-size-[72px] tw-outline-2 tw-outline-offset-1 tw-transition-[outline] hover:tw-outline-primary",
                    {
                      "tw-outline-primary": style === s.value,
                    },
                  )}
                >
                  <img
                    // TODO: add image src
                    src={`/assets/images/ai-generate/${s.image}`}
                    alt={s.label}
                    className="tw-size-full tw-object-cover tw-rounded-lg"
                  />
                </button>
              </Tooltip.Trigger>
              <Tooltip.Content className="tw-z-[99999]">
                <Tooltip.Text className="tw-max-w-60 tw-text-center">
                  {s.label}
                </Tooltip.Text>
              </Tooltip.Content>
            </Tooltip>
          ))}
        </div>
      </Popover.Content>
    </Popover>
  );
};
